/**
 * /particuliers/travaux — Hub catalogue des travaux de rénovation
 * énergétique éligibles aux primes CEE et à MaPrimeRénov'.
 *
 * Refonte 2026 : entièrement bâtie sur le kit UI `audience/particulier`.
 * Sections :
 *  - Hero (PageHero + LiveCard "Top travaux rentables")
 *  - Bandeau KPI animé
 *  - Sections par catégorie (custom — spécifique à ce hub)
 *  - Cross-link vers les 4 parcours (ParcoursNav)
 *  - FAQ (FaqSplit)
 *  - CTA final dark (CTAFinal)
 */

import { Flame, Snowflake, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Container } from '../../../components/layout/Container';
import { Reveal } from '../../../components/motion/Reveal';
import { StaggerGrid, StaggerItem } from '../../../components/motion/StaggerGrid';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import {
  TRAVAUX_PARTICULIERS,
  type TravailStub,
} from '../../../config/travaux-particuliers';
import { TravailMiniCard } from '../../../components/parcours/TravailStubPage';
import { getIcon } from '../../../lib/lucide-icons';
import { easeStripe } from '../../../lib/motion';
import { cn } from '../../../lib/cn';
import {
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierFAQ,
  ParticulierKPIBand,
  ParticulierPageHero,
  ParticulierParcoursNav,
  ParticulierSectionHeader,
  particulierPageUrl,
} from '../../../components/audience/particulier';

const CATEGORY_GROUPS: { id: TravailStub['category']; label: string; eyebrow: string }[] = [
  { id: 'isolation', label: 'Isolation thermique', eyebrow: 'Enveloppe du bâti' },
  { id: 'chauffage', label: 'Chauffage performant', eyebrow: 'ENR & biomasse' },
  { id: 'ecs', label: 'Eau chaude sanitaire', eyebrow: 'Thermodynamique & solaire' },
  { id: 'ventilation', label: 'Ventilation', eyebrow: "Renouvellement d'air" },
];

const TRAVAUX_FAQ = [
  {
    question: "Quel travaux faire en premier pour un maximum d'économies ?",
    answer:
      "L'isolation des combles est généralement le geste le plus rentable : -30 % de chaleur perdue par la toiture, pour un coût modéré (20 à 50 €/m²). Une pompe à chaleur air/eau apporte ensuite les gains les plus importants (-60 % sur facture chauffage), surtout en remplacement d'une chaudière fioul ou gaz. L'idéal : un bouquet de travaux coordonnés pour maximiser MaPrimeRénov’ Accompagné.",
  },
  {
    question: 'Comment savoir si mon artisan est éligible aux aides ?',
    answer:
      "Tous les travaux MaPrimeRénov’ / CEE / éco-PTZ exigent un artisan certifié RGE (Reconnu Garant de l'Environnement) pour le poste concerné : QualiPAC pour les pompes à chaleur, QualiBois pour les poêles à granulés, RGE Qualibat pour l'isolation, QualiSol pour le solaire thermique. Vérifiez la certification sur france-renov.gouv.fr avant de signer.",
  },
  {
    question: 'Faut-il un audit énergétique avant de faire des travaux ?',
    answer:
      "Pas pour un geste isolé (changement d'une PAC seule, isolation des combles seuls). Un audit énergétique réglementaire est obligatoire uniquement pour MaPrimeRénov’ Parcours Accompagné (rénovation d'ampleur visant ≥ 2 sauts DPE). Pour préparer vos travaux, un bilan énergétique gratuit en ligne suffit dans la majorité des cas.",
  },
  {
    question: 'Combien de temps prennent les travaux ?',
    answer:
      "À titre indicatif : isolation des combles 1-2 jours, isolation des murs (ITE) 1-3 semaines, pose d'une PAC air/eau 2-4 jours, poêle à granulés 1-2 jours, VMC double flux 2-5 jours. Les délais d'engagement administratif (demande MPR + CEE avant signature) ajoutent en moyenne 2 à 4 semaines avant le début du chantier.",
  },
  {
    question: "Quelle aide pour le remplacement d'une chaudière fioul ?",
    answer:
      "Le remplacement d'une chaudière fioul est l'opération la plus aidée en 2026 : MaPrimeRénov’ (jusqu'à 5 000 €), Prime CEE (jusqu'à 4 200 €), Coup de pouce Chauffage (jusqu'à 4 000 € de bonification pour les ménages modestes). Sur une PAC air/eau, le cumul peut couvrir 70 à 90 % du coût.",
  },
];

export default function ParticuliersTravauxHubPage() {
  const topTravaux = [...TRAVAUX_PARTICULIERS]
    .sort((a, b) => b.savingsPercent - a.savingsPercent)
    .slice(0, 3);
  const maxSavings = Math.max(...TRAVAUX_PARTICULIERS.map((t) => t.savingsPercent));
  const totalAidesMax = TRAVAUX_PARTICULIERS.reduce((sum, t) => sum + t.maxAidEur, 0);

  const kpi = [
    {
      value: `Jusqu'à −${maxSavings}%`,
      numericValue: maxSavings,
      suffix: '%',
      label: 'sur votre facture de chauffage (PAC air/eau)',
      source: "Remplacement d'une chaudière fioul ou gaz",
    },
    {
      value: `${totalAidesMax.toLocaleString('fr-FR')} €`,
      numericValue: totalAidesMax,
      suffix: ' €',
      label: "d'aides max cumulables tous gestes",
      source: 'Pour les ménages aux revenus modestes',
    },
    {
      value: '5 aides',
      numericValue: 5,
      label: 'cumulables sur chaque opération',
      source: "MaPrimeRénov’ + CEE + éco-PTZ + TVA + Coup de pouce",
    },
  ];

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${PARTICULIER_URLS.site}/` },
          { name: 'Particuliers', url: `${PARTICULIER_URLS.site}${PARTICULIER_URLS.home}` },
          { name: 'Travaux', url: particulierPageUrl('travaux') },
        ]}
      />

      <ParticulierPageHero
        breadcrumb={[{ name: 'Travaux' }]}
        eyebrow={`Catalogue 2026 · ${TRAVAUX_PARTICULIERS.length} opérations`}
        eyebrowIconName="sparkles"
        headline="Tous les travaux"
        headlineAccent="éligibles aux aides 2026"
        subheadline={
          <>
            Isolation, pompe à chaleur, poêle à granulés, chauffe-eau thermodynamique ou
            solaire, VMC double flux : {TRAVAUX_PARTICULIERS.length} opérations couvertes par
            MaPrimeRénov’, les primes CEE et l'éco-PTZ. Pour chaque geste, on chiffre les
            économies et le reste à charge.
          </>
        }
        primaryCta={{ label: 'Calculer mes aides en 2 min', href: PARTICULIER_URLS.calculateur }}
        secondaryCta={{ label: 'Voir les fiches CEE officielles', href: '/cee/fiches?secteur=BAR' }}
        bullets={[
          'Devis gratuit · sans engagement',
          'Aides 2026 à jour',
          'Artisan RGE près de chez vous',
        ]}
        sideSlot={<TopTravauxPreview travaux={topTravaux} />}
      />

      <ParticulierKPIBand items={kpi} />

      {CATEGORY_GROUPS.map((group, idx) => {
        const items = TRAVAUX_PARTICULIERS.filter((t) => t.category === group.id);
        if (items.length === 0) return null;
        return (
          <section
            key={group.id}
            className={cn(
              'py-16 md:py-24',
              idx % 2 === 1 ? 'border-y border-stone-100 bg-stone-50/30' : '',
            )}
          >
            <Container>
              <ParticulierSectionHeader
                eyebrow={`${group.eyebrow} · ${items.length} opération${items.length > 1 ? 's' : ''}`}
                title={group.label}
                align="left"
              />

              <StaggerGrid className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((travail) => (
                  <StaggerItem key={travail.slug}>
                    <TravailMiniCard travail={travail} />
                  </StaggerItem>
                ))}
              </StaggerGrid>
            </Container>
          </section>
        );
      })}

      <ParticulierParcoursNav
        eyebrow="Parcours rénovation"
        title="De l'idée au chantier : 4 étapes claires"
        description="Avant de choisir vos travaux, identifiez le bon point de départ. Notre parcours vous accompagne du bilan initial au versement des aides."
        variant="compact"
      />

      <ParticulierFAQ
        eyebrow="FAQ travaux & aides"
        title="Vos questions avant de lancer vos travaux."
        items={TRAVAUX_FAQ}
      />

      <ParticulierCTAFinal
        badge="Plan de financement personnalisé"
        title="Quelle combinaison d'aides"
        titleAccent="pour vos travaux ?"
        description="Notre calculateur chiffre MaPrimeRénov’ + Prime CEE + éco-PTZ + TVA 5,5 % selon votre logement, vos revenus et l'ensemble de vos gestes — gratuit, sans inscription."
        primaryCta={{ label: 'Lancer le calculateur', href: PARTICULIER_URLS.calculateur }}
        secondaryCta={{ label: 'Être rappelé(e)', href: PARTICULIER_URLS.contact }}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// TopTravauxPreview — side card hero (top 3 travaux par économies)
// Spécifique à ce hub (illustration "live" contextuelle).
// ════════════════════════════════════════════════════════════════

function TopTravauxPreview({ travaux }: { travaux: TravailStub[] }) {
  return (
    <Reveal delay={0.18}>
      <div className="relative">
        <div
          aria-hidden
          className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-brand-100/40 via-accent-100/30 to-transparent blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 16 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
          className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-brand-200/60 md:p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                Top travaux rentables
              </p>
              <p className="mt-0.5 text-xs text-stone-500">Classement par économies sur facture</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50">
              <span className="h-1 w-1 rounded-full bg-brand-500 animate-glow-pulse" />
              2026
            </span>
          </div>

          <div className="mt-5 space-y-3">
            {travaux.map((t, idx) => {
              const Icon = getIcon(t.iconName);
              return (
                <motion.a
                  key={t.slug}
                  href={`/particuliers/travaux/${t.slug}`}
                  initial={{ opacity: 0, x: -6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.4, delay: 0.3 + idx * 0.08, ease: easeStripe }}
                  className="group flex items-center gap-3 rounded-xl border border-stone-200/70 bg-stone-50/40 p-3 transition-all duration-150 ease-out hover:border-brand-200 hover:bg-paper hover:shadow-[0_4px_16px_-4px_rgba(10,10,10,.06)]"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-paper text-brand-700 ring-1 ring-inset ring-brand-200/60">
                    <span className="font-mono text-[10px] font-bold tabular-nums">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-ink">{t.title}</p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <Icon className="h-3 w-3 text-stone-400" strokeWidth={1.8} />
                      <p className="truncate text-[10.5px] text-stone-500">
                        {t.maxAidEur.toLocaleString('fr-FR')} € d'aides max
                      </p>
                    </div>
                  </div>
                  <span className="ml-1 inline-flex items-center gap-0.5 rounded-md bg-gradient-to-br from-brand-50 to-accent-50/40 px-2 py-1 ring-1 ring-inset ring-brand-200/40">
                    <TrendingDown className="h-3 w-3 text-brand-700" strokeWidth={2.4} />
                    <span className="font-display text-[11px] font-bold text-brand-800 tabular-nums">
                      −{t.savingsPercent}%
                    </span>
                  </span>
                </motion.a>
              );
            })}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 border-t border-stone-200/60 pt-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                <Snowflake className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                  Hiver
                </p>
                <p className="truncate text-[11px] font-semibold text-ink">+3°C ressentis</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-accent-50 text-accent-700 ring-1 ring-inset ring-accent-200/60">
                <Flame className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                  Facture
                </p>
                <p className="truncate text-[11px] font-semibold text-ink">−60% chauffage</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Reveal>
  );
}
